import React, { useEffect, useState } from 'react';
import { Button } from './ui/button';
import { ShoppingCart, AlertTriangle, CheckCircle2, RefreshCw } from 'lucide-react';
import { api } from '../utils/api';
import { useTheme } from '../contexts/ThemeContext';
import { BuyCreditsModal } from './BuyCreditsModal';

/**
 * BILLING-02 — UsageMeter for the dashboard header.
 * Shows progress bar with color-coded states + credits balance + "Comprar mais" CTA at ≥70%.
 */
export const UsageMeter = () => {
  const { isDark } = useTheme();
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [creditsOpen, setCreditsOpen] = useState(false);

  useEffect(() => {
    let mounted = true;
    const fetchUsage = async () => {
      try {
        const data = await api.getUsageStats();
        if (mounted) setUsage(data);
      } catch {
        if (mounted) setUsage(null);
      } finally {
        if (mounted) setLoading(false);
      }
    };
    fetchUsage();
    return () => { mounted = false; };
  }, []);

  if (loading || !usage) {
    return (
      <div
        className={`rounded-xl p-4 border ${isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-200'}`}
        data-testid="usage-meter-loading"
      >
        <div className="h-4 w-40 bg-slate-300 dark:bg-slate-700 rounded animate-pulse" />
        <div className="h-2 bg-slate-200 dark:bg-slate-700 rounded mt-3 animate-pulse" />
      </div>
    );
  }

  const used = usage.consultations_used || 0;
  const included = usage.consultations_included || 0;
  const credits = usage.credits_balance || 0;
  const pct = Math.min(100, Math.round((usage.percentage_used || 0) * 10) / 10);
  const canBuyCredits = !!usage.can_buy_credits;
  const isAtLimit = used >= (included + credits);
  const remaining = Math.max(0, included - used);

  // Color tier
  let barClass = 'bg-emerald-500';
  let textTier = 'Tudo certo';
  let textColor = 'text-emerald-600 dark:text-emerald-400';
  if (isAtLimit) {
    barClass = 'bg-red-500';
    textTier = 'Limite atingido';
    textColor = 'text-red-600 dark:text-red-400';
  } else if (pct >= 90) {
    barClass = 'bg-orange-500';
    textTier = `Quase no limite: ${remaining} consulta${remaining === 1 ? '' : 's'} restante${remaining === 1 ? '' : 's'}`;
    textColor = 'text-orange-600 dark:text-orange-400';
  } else if (pct >= 70) {
    barClass = 'bg-amber-500';
    textTier = `Atenção: ${remaining} consulta${remaining === 1 ? '' : 's'} restante${remaining === 1 ? '' : 's'}`;
    textColor = 'text-amber-600 dark:text-amber-400';
  }

  const resetDate = usage.reset_date ? new Date(usage.reset_date) : null;
  const resetLabel = resetDate
    ? resetDate.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' })
    : null;

  const showBuyButton = canBuyCredits && (pct >= 70 || isAtLimit);

  return (
    <>
      <div
        className={`rounded-xl p-5 border ${isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-200'}`}
        data-testid="usage-meter"
      >
        <div className="flex flex-wrap items-center justify-between gap-3 mb-2">
          <div>
            <div className={`text-sm font-semibold ${isDark ? 'text-slate-200' : 'text-slate-700'}`}>
              <span data-testid="usage-meter-text">
                {remaining} de {included} do seu plano
                {credits > 0 ? ` + ${credits} crédito${credits === 1 ? '' : 's'} avulso${credits === 1 ? '' : 's'}` : ''}
              </span>
            </div>
            <div className={`text-xs mt-0.5 ${textColor}`} data-testid="usage-meter-tier">
              {isAtLimit ? <AlertTriangle className="inline h-3 w-3 mr-1" /> : <CheckCircle2 className="inline h-3 w-3 mr-1" />}
              {textTier}
            </div>
          </div>
          {showBuyButton && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setCreditsOpen(true)}
              data-testid="usage-meter-buy-credits-btn"
            >
              <ShoppingCart className="h-4 w-4 mr-2" />
              Comprar mais consultas
            </Button>
          )}
        </div>

        <div
          className={`h-2 rounded-full overflow-hidden ${isDark ? 'bg-slate-700' : 'bg-slate-200'}`}
          data-testid="usage-meter-bar"
        >
          <div
            className={`h-full ${barClass} transition-all`}
            style={{ width: `${pct}%` }}
            data-testid="usage-meter-fill"
          />
        </div>

        <div className="flex flex-wrap items-center justify-between gap-2 mt-2">
          <span />
          {resetLabel && (
            <span
              className={`text-xs flex items-center gap-1 ${isDark ? 'text-slate-500' : 'text-slate-400'}`}
              data-testid="usage-meter-reset"
            >
              <RefreshCw className="h-3 w-3" /> Renova em {resetLabel}
            </span>
          )}
        </div>
      </div>

      <BuyCreditsModal
        open={creditsOpen}
        onOpenChange={setCreditsOpen}
        canBuyCredits={canBuyCredits}
      />
    </>
  );
};
